import React, { useState, useRef } from 'react';
import Button from './Button';
import { 
  HiLightningBolt, 
  HiQuestionMarkCircle, 
  HiCheckCircle, 
  HiChartBar, 
  HiRefresh 
} from 'react-icons/hi';

const PollCard = ({ poll, onToggleOnAir, onAsk, onEdit, onDelete, onResetResults, onToggleDisplayMode }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleToggleOnAir = () => {
    console.log('Toggle on air clicked for poll:', poll.id, 'current status:', poll.is_on_air);
    onToggleOnAir(poll.id, !poll.is_on_air);
    setShowMenu(false);
  };

  const handleAsk = () => {
    console.log('Ask clicked for poll:', poll.id, 'current status:', poll.is_asked);
    onAsk(poll.id, !poll.is_asked);
    setShowMenu(false);
  };

  const handleEdit = () => {
    onEdit(poll);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this poll?')) {
      onDelete(poll.id);
      setShowMenu(false);
    }
  };

  const handleResetResults = () => {
    if (window.confirm('Are you sure you want to reset the poll results? This will clear all votes.')) {
      onResetResults(poll.id);
      setShowMenu(false);
    }
  };

  const handleToggleDisplayMode = () => {
    onToggleDisplayMode(poll.id);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg shadow-lg border-2 transition-all hover:shadow-xl relative flex flex-col h-full ${
      poll.is_on_air ? 'border-red-500 bg-gradient-to-br from-red-50 to-red-100 shadow-red-200 shadow-lg' : 
      poll.is_asked ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100' : 'border-gray-300 shadow-gray-200'
    }`}>
      {/* Status Corner Indicator */}
      {(poll.is_on_air || poll.is_asked) && (
        <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
          poll.is_on_air ? 'bg-red-500 animate-pulse' : 'bg-blue-500'
        }`} title={poll.is_on_air ? 'ON AIR' : 'ASKED'}></div>
      )}
      {/* Content Area */}
      <div className="flex-grow">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
              {poll.question}
            </h3>
          </div>
          
          <div className="relative z-30 ml-4" ref={menuRef}>
          <button
            onClick={toggleMenu}
            className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100 relative z-30"
            aria-label="Poll options"
            style={{pointerEvents: 'auto'}}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          
          {showMenu && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-40 border border-gray-200">
              <div className="py-1">
                <button
                  onClick={handleEdit}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Options with Integrated Results */}
        <div className="mb-4">
        <div className="space-y-2">
          {poll.options.map((option, index) => {
            // Find result for this option
            const result = poll.results?.find(r => r.option_index === index);
            const totalVotes = poll.results?.reduce((sum, r) => sum + r.count, 0) || 0;
            const percentage = totalVotes > 0 ? Math.round((result?.count || 0) / totalVotes * 100) : 0;
            const hasVotes = result && result.count > 0;
            
            return (
              <div key={index} className="relative">
                        <div 
                          className={`text-sm text-gray-700 px-3 py-2 rounded-lg transition-all duration-300 ${
                            hasVotes 
                              ? 'bg-blue-100 border border-blue-300 shadow-sm' 
                              : 'bg-gray-100 border border-gray-300 shadow-sm'
                          }`}
                  style={{
                    background: hasVotes 
                      ? `linear-gradient(to right, rgba(59, 130, 246, ${percentage / 100 * 0.3}), rgba(59, 130, 246, ${percentage / 100 * 0.1}))`
                      : undefined
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-600 font-bold text-xs">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="font-medium">{option}</span>
                    </div>
                    {hasVotes && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-blue-600 font-semibold">
                          {result.count}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({percentage}%)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Total votes summary */}
        {poll.results && poll.results.length > 0 && (
          <div className="mt-2 text-xs text-gray-500 text-center">
            📊 {poll.results.reduce((sum, r) => sum + r.count, 0)} total votes
          </div>
        )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-300 my-4"></div>

      {/* Actions - Compact Icon Buttons */}
      <div className="flex items-center justify-between space-x-3">
        {/* On Air Button */}
        <button
          onClick={(e) => {
            console.log('Set On Air button clicked!', e);
            handleToggleOnAir();
          }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 ${
                    poll.is_on_air 
                      ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg' 
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-sm'
                  }`}
          title={poll.is_on_air ? 'On Air' : 'Set On Air'}
        >
          <HiLightningBolt className="w-5 h-5" />
        </button>

        {/* Ask Button */}
        <button
          onClick={(e) => {
            console.log('Ask button clicked!', e);
            handleAsk();
          }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 ${
                    poll.is_asked 
                      ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg' 
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-sm'
                  }`}
          title={poll.is_asked ? 'Asked' : 'Ask Question'}
        >
          {poll.is_asked ? (
            <HiCheckCircle className="w-5 h-5" />
          ) : (
            <HiQuestionMarkCircle className="w-5 h-5" />
          )}
        </button>

                {/* Display Mode Toggle */}
                <div className="flex items-center bg-gray-200 rounded-full p-1 shadow-sm">
          <button
            onClick={handleToggleDisplayMode}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
              poll.displayMode === 'question' 
                ? 'bg-white shadow-sm text-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Question Mode"
          >
            <HiQuestionMarkCircle className="w-4 h-4" />
          </button>
          <button
            onClick={handleToggleDisplayMode}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
              poll.displayMode === 'results' 
                ? 'bg-white shadow-sm text-green-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Results Mode"
          >
            <HiChartBar className="w-4 h-4" />
          </button>
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetResults}
                  className="w-10 h-10 rounded-full bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-sm flex items-center justify-center transition-all duration-200 hover:scale-105"
          title="Reset Results"
        >
          <HiRefresh className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default PollCard;
